import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { videos, categories } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug]
  );

  const filteredVideos = useMemo(() => {
    if (activeSlug === 'all') return videos;
    return videos.filter((v) => v.category === activeSlug);
  }, [activeSlug]);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类浏览', path: '/category/all' },
    { label: activeCategory.name },
  ];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCategory.name} - 在线观看免费高清`,
            description: `在线观看${activeCategory.name}分类，海量高清内容免费观看，支持多终端同步，流畅不卡顿。`,
            url: `https://www.zaixianguankan.com/category/${activeSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '在线观看',
              url: 'https://www.zaixianguankan.com',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <div className="pt-20 pb-8 bg-background-100/50 border-b border-background-200/60">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mt-4">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              共 {filteredVideos.length} 部内容 · 在线观看免费高清
            </p>
          </div>
        </div>

        <div className="py-8">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-3 scrollbar-hide mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {filteredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/category/${video.category}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-video bg-background-200 mb-3">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      title={`${video.title} - 在线观看免费高清`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <span className="text-xs text-white bg-primary-500/90 px-2 py-0.5 rounded">
                        {video.quality}
                      </span>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="text-xs text-white bg-black/70 px-2 py-0.5 rounded">
                        {video.duration}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-1 text-white text-xs bg-black/70 px-2 py-0.5 rounded">
                      <i className="ri-star-fill text-primary-500 text-xs"></i>
                      {video.rating}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {video.title}
                  </h3>
                  <p className="text-xs text-foreground-600 mt-1 line-clamp-1">{video.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-foreground-600">{video.year}</span>
                    <span className="text-xs text-foreground-600">{video.views}次播放</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {video.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>

            {filteredVideos.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-film-line text-2xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}